import {useEffect, useRef, useState} from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {Box, Button, Drawer, Grid, Typography} from "@mui/material";
import {Shipping} from "../Shipping/shipping";
import {OrderDocuments} from "../Job/JobManagement/JobDocuments/jobDocuments";
import {AdminFee} from "../../../component/admin/fee/adminFee";
import {Job} from "../Job/Job";
import {AdminTapeForm} from "../Tape/tape";
import {AdminIngestionForm} from "../Ingestion/ingestion";
import {flexStyles} from "../../../component/theme/theme";
import {useFieldArray, useForm} from "react-hook-form";
import moment from "moment";
import Snackbar from "@mui/material/Snackbar";
import {Link, useLocation} from "react-router";
import {myFunction} from "../../../global";
import {AdminAccountCreationSuccess} from "../AccountCreation/AdminAccountCreationSuccess";
import {addShipment, addTape, createJob, deleteShipment, fetchAllCustomerContacts, fetchCustomer, fetchCustomers, fetchCustomerUsers, fetchJob, fetchJobDocuments, fetchShipmentDetail, fetchTapes, patchJob, patchShipment, updateTape} from "../../../utils/apiCalls";
import {ConfirmDialog, StyledButtonContained, StyledButtonOutlined, WizardStepConnector, WizardStepIcon,} from "../../../utils/uiUtils";

const steps = ["Job", "Shipping", "Tape", "Ingestion", "Fee", "Documents"];

/**
 * A date in the form the API accepts, or null where the form holds no date.
 *
 * The guard is the point of this, because moment() of a missing value is not itself missing, and the two
 * kinds of missing fail differently. moment(undefined) is *now*, so an untouched optional date field used
 * to save today's date silently — a completion date nobody entered. moment(null), which is what the v6
 * picker reports while an entry is incomplete, is an invalid moment, and format() on an invalid moment
 * returns the literal string 'Invalid date'; posting that earned "Date has wrong format. Use one of these
 * formats instead: YYYY-MM-DD." from the API, an error message that misdescribes its own cause.
 *
 * @param value - whatever the form holds: a moment, a date string, null or undefined.
 * @returns {string|null} the date as YYYY-MM-DD, or null.
 */
const apiDate = (value) => (value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : null);


export const JobWizard = ({
                                externalForm,
                                externalActiveStep,
                                onActiveStepChange,
                                customerId,
                                onComplete,
                                showStepper = true
                            }) => {
    // --- State ---
    const [disableSaveAndNext, setDisableSaveAndNext] = useState(false);
    const [shippingMode, setShippingMode] = useState("tapeark");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const [orderDetail, setOrderDetail] = useState();

    const location = useLocation();
    const [orderId, setOrderId] = useState(null);
    const [shippingLocations, setShippingLocations] = useState([]);
    const [customerAddress, setCustomerAddress] = useState(null);
    const [customerUsers, setCustomerUsers] = useState({platform: [], contacts: []});
    const ingestionFormRef = useRef();
    const [internalStep, setInternalStep] = useState(0);
    const [isSkipShippingDialogOpen, setIsSkipShippingDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [customers, setCustomers] = useState(null);

    const activeStep = externalActiveStep !== undefined ? externalActiveStep : internalStep;
    const getOrderUUID = () => window?.location?.href?.split("uuid=")[1];

    // --- Form ---
    const internalForm = useForm({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            shippingLinks: [{link: ""}],
            tape: {
                tapeDetails: [{tapeType: "", number: 0, tapeFormat: ""}],
            },
        },
    });
    const form = externalForm || internalForm;
    const {
        register,
        control,
        handleSubmit,
        setValue,
        getValues,
        watch,
        reset,
        formState: {errors},
    } = form;

    const {
        fields: shippingLinksFields,
        append: shippingLinksAppend,
        remove: shippingLinksRemove,
    } = useFieldArray({control, name: "shippingLinks"});

    // --- Snackbar ---
    const openSnackbar = (message) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    // --- Initial load ---
    useEffect(() => {
        if (customerId) {
            setValue("tenant_id", customerId);
        } else if (location?.state?.uuid) {
            setValue("tenant_id", location?.state?.uuid);
        }
        fetchCustomers().then(setCustomers).catch(() => {});
        loadStepData();
    }, []);

    // Load step data when controlled step changes externally
    useEffect(() => {
        if (externalActiveStep !== undefined) {
            loadStepData(externalActiveStep);
        }
    }, [externalActiveStep]);

    // --- Step data loaders ---
    const loadJobData = (orderUUID) => {
        setValue("isJobStepLoading", true);
        fetchJob(orderUUID)
            .then((orderData) => {
                setValue("tenant_id", orderData.customer);
                setValue("project_deliverables", orderData.project_deliverables);
                setValue("project_scope", orderData.project_scope);
                setValue("tapeark_job_id", orderData.tapeark_job_id);
                setValue("quote_reference_id", orderData.quote_reference_id);
                setValue("security_privace", orderData.job_security_privacy);
                setValue("sow_commence", orderData.sow_start);
                setValue("sow_complete", orderData.sow_end);
                setValue("billing", orderData.billing);
                setValue("billing_description", orderData.billing_description ?? null);
                setValue("isJobStepLoading", false);
            })
            .catch((error) => {
                console.log(error);
                setValue("isJobStepLoading", false);
            });
    };

    const loadShippingData = (orderUUID) => {
        fetchJob(orderUUID)
            .then((orderData) => {
                setOrderDetail(orderData);
                if (orderData.customer) {
                    fetchCustomer(orderData.customer)
                        .then(c => { if (c?.contact_address?.address_line_1) setCustomerAddress({...c.contact_address, _label: c.company_name}); })
                        .catch(() => {});
                    fetchAllCustomerContacts(orderData.customer)
                        .then(result => setCustomerUsers(result))
                        .catch(() => {});
                }
                setValue("chainOfCustody", orderData.chain_of_custody);
                let _setShipping_mode = orderData?.shipping_mode ? orderData?.shipping_mode : "tapeark";
                setValue("shippingMode", _setShipping_mode);
                setShippingMode(_setShipping_mode);
                let allShipments = [];
                let isRemoveDefaultData = false;
                orderData?.shipments?.length &&
                orderData?.shipments?.forEach((element) => {
                    if (element.first_name) {
                        allShipments.push(fetchShipmentDetail(orderUUID, element.uuid));
                    } else {
                        if (!isRemoveDefaultData) {
                            isRemoveDefaultData = true;
                            shippingLinksRemove(0);
                        }
                        setTimeout(() => {
                            shippingLinksAppend({link: element.tracking_link, uuid: element.uuid});
                        }, 10);
                    }
                });
                getShipmetDetails(allShipments);
            })
            .catch((error) => console.log(error));
    };

    const loadTapeData = (orderUUID) => {
        setValue("isTapeStepLoading", true);
        setValue("isIngestionStepLoading", true);
        setValue("tape.accountOptions", "enterprise");

        fetchJob(orderUUID)
            .then((orderData) => {
                setValue("tape.numberOfBoxes", orderData.number_of_boxes);
                setValue("tape.volserAvailable", orderData.volser_available);
                setValue("tape.backupType", orderData.backup_type);
                setValue("tape.accountOptions", orderData.account ? orderData.account : "enterprise");

                fetchTapes(orderUUID)
                    .then((tapeResponse) => {
                        if (tapeResponse?.results?.length > 0) {
                            tapeResponse.results.map((tape, index) => {
                                setValue(`tape.tapeDetails[${index}].tapeType`, tape.type);
                                setValue(`tape.tapeDetails[${index}].number`, tape.number);
                                setValue(`tape.tapeDetails[${index}].tapeFormat`, tape.backup_format);
                                setValue(`tape.tapeDetails[${index}].uuid`, tape.uuid);
                            });
                        }

                        fetchJobDocuments(orderUUID)
                            .then((documentResponse) => {
                                if (documentResponse?.results) {
                                    const documents = JSON.parse(JSON.stringify(documentResponse.results));
                                    documents.map((document, index) => {
                                        if (document.file_type === "tape_list") {
                                            setValue(
                                                "tape.orderdocuments.tapeList",
                                                new File([""], document.name, {
                                                    type: document.mime_type,
                                                    lastModified: new Date(),
                                                })
                                            );
                                            setValue("tape.orderdocuments.tLuuid", document.uuid);
                                        }
                                    });
                                }
                                setValue("isTapeStepLoading", false);
                            })
                            .catch((err) => {
                                console.log(err);
                                setValue("isTapeStepLoading", false);
                            });
                    })
                    .catch((err) => {
                        console.log(err);
                        setValue("isTapeStepLoading", false);
                    });

                setValue("isIngestionStepLoading", false);
            })
            .catch((err) => {
                console.log(err);
                setValue("isTapeStepLoading", false);
                setValue("isIngestionStepLoading", false);
            });
    };

    const loadIngestionData = (orderUUID) => {
        setValue("isTapeStepLoading", true);
        setValue("isIngestionStepLoading", true);

        fetchJob(orderUUID)
            .then((orderData) => {
                setValue("ingestion.postIngestion", orderData.post_ingestion);
                setValue("ingestion.destroyMode", orderData.destroy_mode);
                setValue("ingestion.storageDuration", orderData.storage_duration);
                setValue("ingestion.tapeOptions", orderData.tape_security_privacy);
                setValue("ingestion.returnTo", orderData.tape_return_to_sender);

                if (orderData.tape_return_to_sender === false) {
                    if (orderData?.tape_return_address) {
                        setValue("ingestion.returnAddress.address_line_1", orderData.tape_return_address.address_line_1);
                        setValue("ingestion.returnAddress.postal_code", orderData.tape_return_address.postal_code);
                        setValue("ingestion.returnAddress.country", orderData.tape_return_address.country);
                        setValue("ingestion.returnAddress.city", orderData.tape_return_address.city);
                        setValue("ingestion.returnAddress.state", orderData.tape_return_address.state);
                    }
                    setValue("ingestion.returnContact.first_name",    orderData.tape_return_first_name    ?? '');
                    setValue("ingestion.returnContact.last_name",     orderData.tape_return_last_name     ?? '');
                    setValue("ingestion.returnContact.phone_number",  orderData.tape_return_phone_number  ?? '');
                    setValue("ingestion.returnContact.mobile_number", orderData.tape_return_mobile_number ?? '');
                    setValue("ingestion.returnContact.email",         orderData.tape_return_email         ?? '');
                }

                setValue("isIngestionStepLoading", false);
            })
            .catch((err) => {
                console.log(err);
                setValue("isTapeStepLoading", false);
                setValue("isIngestionStepLoading", false);
            });
    };

    const loadStepData = (step = 0) => {
        const _orderUUID = getOrderUUID();
        if (!_orderUUID) return;

        switch (step) {
            case 0:
                return loadJobData(_orderUUID);
            case 1:
                return loadShippingData(_orderUUID);
            case 2:
                return loadTapeData(_orderUUID);
            case 3:
                return loadIngestionData(_orderUUID);
        }
    };

    // --- Helper functions ---
    const removeShipmentLink = async (index, uuid) => {
        const _orderUUID = getOrderUUID();
        deleteShipment(_orderUUID, uuid, {customerId: orderDetail?.customer})
            .then(() => {
                shippingLinksRemove(index);
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    const getShipmetDetails = (allRequest) => {
        Promise.all(allRequest)
            .then((results) => {
                let shipmentLocations = [];
                results.forEach((shipmentData) => {
                    if (shipmentData.shipping_address) {
                        let element = {
                            uuid: shipmentData.uuid,
                            tempId: new Date().valueOf(),
                            firstName: shipmentData.first_name,
                            lastName: shipmentData.last_name,
                            phone: shipmentData.phone_number,
                            mobile: shipmentData.mobile_number,
                            optional_email: shipmentData.optional_email,
                            pickup_date: shipmentData.pickup_date,

                            address_uuid: shipmentData.shipping_address.uuid,
                            country: shipmentData.shipping_address.country,
                            city: shipmentData.shipping_address.city,
                            state: shipmentData.shipping_address.state,
                            postal_code: shipmentData.shipping_address.postal_code,
                            address_line_1: shipmentData.shipping_address.address_line_1,
                            ingestionSite: shipmentData.ingestion_site,
                            shippingPartner: shipmentData.courier_partner,
                        };
                        shipmentLocations.push(element);
                    } else {
                        shipmentData.tracking_links.forEach((element) => {
                            shippingLinksAppend({link: element.link});
                        });
                    }
                });
                setShippingLocations(shipmentLocations);
            })
            .catch((error) => console.log(error));
    };

    /*
     * Compares the two dates as dates. It used to format both to 'DD-MM-YYYY' and compare the resulting
     * strings, which ranks by day of the month and only reaches the month when the days are equal. So
     * 20 January to 5 June was judged out of order — and case 0 below simply does not save when this
     * returns false, with no message, so Save appeared to do nothing — while 22 June to 25 January passed.
     *
     * It also admitted the one value it existed to catch: an absent or incomplete date formats to the
     * literal string 'Invalid date', and every digit sorts below 'I', so the comparison was true and the
     * string was posted to the API, which rejected it with "Date has wrong format".
     *
     * A blank completion date is valid. The field is optional — only Project Commence is required — and
     * the payload guard in jobCreation() sends null for it. Equal dates are valid too: a job may start
     * and finish on one day.
     */
    const isStepValid = () => {
        const commence = watch("sow_commence") ? moment(watch("sow_commence")) : null;
        const complete = watch("sow_complete") ? moment(watch("sow_complete")) : null;
        if (!complete?.isValid()) return true;
        return !!commence?.isValid() && commence.isSameOrBefore(complete, 'day');
    };

    // --- Main form handler (0-based: Job=0, Shipping=1, Tape=2, Ingestion=3, Fee=4, Documents=5) ---
    const handleFormData = (data, event) => {
        const isNext = event?.nativeEvent?.submitter?.value === "saveAndContinue";
        switch (activeStep) {
            case 0: if (isStepValid()) jobCreation(data, isNext); break;
            case 1: shippingCreation(data, isNext); break;
            case 2: tapeCreation(data.tape, isNext); break;
            case 3: {
                const ingestionValid = ingestionFormRef.current?.validate() ?? true;
                if (ingestionValid) ingestionCreation(data.ingestion, isNext);
                break;
            }
            case 4: if (isNext) { handleNext(); openSnackbar("Data added successfully."); } break;
            case 5: orderDocumentSubmission(data, isNext); break;
        }
    };

    // --- Shipment handlers ---
    const clearAddShipmentField = () => {
        setValue("shippingLocations_firstName", null);
        setValue("shippingLocations_lastName", null);
        setValue("optional_email", null);
        setValue("pickup_date", null);
        setValue("shippingLocations_phone", null);
        setValue("shippingLocations_mobile", null);
        setValue("shippingLocations_country", null);
        setValue("shippingLocations_city", null);
        setValue("shippingLocations_state", null);
        setValue("shippingLocations_postal_code", null);
        setValue("shippingLocations_address_line_1", null);
        setValue("shippingLocations_ingestionSite", "");
    };

    let handleSubmitShipment = (element) => {
        if (!element.pickup_date) {
            openSnackbar("Please add date.");
            return;
        }
        const _orderUUID = getOrderUUID();
        let shipmentPayload = {
            shipping_address: {
                address_line_1: element.address_line_1,
                address_line_2: "",
                country: element.country,
                state: element.state,
                postal_code: element.postal_code,
                city: element.city,
            },
            first_name: element.firstName,
            last_name: element.lastName,
            phone_number: element.phone,
            mobile_number: element.mobile,
            courier_partner: element.shippingPartner,
            optional_email: element.optional_email,
            pickup_date: apiDate(element.pickup_date),
            ingestion_site: element.ingestionSite,
            order: _orderUUID,
            ...(element.uuid && {uuid: element.uuid}),
        };
        submitShipment(shipmentPayload, element);
    };

    let handleRemoveShipment = async (index, data) => {
        const _orderUUID = getOrderUUID();
        deleteShipment(_orderUUID, data[index]?.uuid, {customerId: orderDetail?.customer})
            .then((response) => {
                shippingLocations.splice(index, 1);
                data.splice(index, 1);
                setShippingLocations(data);
                openSnackbar("Data deleted successfully.");
            })
            .catch((err) => console.log(err));
    };

    let submitShipment = async (data, element) => {
        let shipmentPayload = data;
        if (!data.uuid) {
            addShipment(shipmentPayload, {customerId: orderDetail?.customer})
                .then((res) => {
                    if (element?.firstName) {
                        clearAddShipmentField();
                    }
                    element["uuid"] = res.uuid;
                    setShippingLocations([...shippingLocations, element]);
                    openSnackbar("Data added successfully.");
                })
                .catch(function (error) {
                    console.log(error);
                });
        } else {
            patchShipment(shipmentPayload.order, shipmentPayload.uuid, shipmentPayload, {customerId: orderDetail?.customer})
                .then(() => {
                    let _shippingLocations = JSON.parse(
                        JSON.stringify(shippingLocations)
                    );
                    if (element?.firstName) {
                        let _index = _shippingLocations.findIndex(
                            (item) => item.uuid === shipmentPayload.uuid
                        );
                        if (-1 != _index) {
                            _shippingLocations[_index] = element;
                            setShippingLocations(_shippingLocations);
                        }
                    }
                    openSnackbar("Data updated successfully.");
                })
                .catch(function (error) {
                    console.log(error);
                });
        }
    };

    // --- Step handlers ---
    const shippingCreation = (data, isNext) => {
        const _orderUUID = getOrderUUID();

        fetchJob(_orderUUID)
            .then((orderDetailResponse) => {
                setOrderDetail(orderDetailResponse);
                const resolvedCustomerId = orderDetailResponse?.customer || customerId;
                const shippingPayload = {
                    chain_of_custody: data.chainOfCustody,
                    shipping_mode: data.shippingMode,
                    customer: resolvedCustomerId,
                    shipping_location: "single",
                    external_pickup_description: data.externalShipmentDescription,
                };
                patchJob(_orderUUID, resolvedCustomerId, shippingPayload)
                    .then(() => {
                        if (
                            orderDetailResponse?.shipping_mode == "tapeark" &&
                            !orderDetailResponse?.shipments?.length
                        ) {
                            myFunction("Please add shipment before proceed.");
                        } else {
                            openSnackbar("Data added successfully.");
                            isNext && handleNext();
                        }
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
            })
            .catch((error) => {
                console.log(error);
                setValue("isJobStepLoading", false);
            });

        if (data.shippingMode == "self") {
            data.shippingLinks.forEach((element) => {
                let shipmentPayload = {
                    tracking_link: element.link,
                    pick_up_date: apiDate(data.pickUpDate),
                    order: _orderUUID,
                    uuid: element.uuid,
                };
                submitShipment(shipmentPayload);
            });
        }
    };

    const jobCreation = (data, isNext) => {
        let orderCreationPayload = {
            tapeark_job_id: data.tapeark_job_id,
            quote_reference_id: data.quote_reference_id,
            project_scope: data.project_scope,
            project_deliverables: data.project_deliverables,
            job_security_privacy: data.security_privace,
            sow_start: apiDate(data.sow_commence),
            sow_end: apiDate(data.sow_complete),
            billing: data.billing,
            billing_description: data.billing === 'other' ? (data.billing_description ?? null) : null,
        };
        const _orderUUID = getOrderUUID();
        if (_orderUUID && _orderUUID !== "undefined") {
            patchJob(_orderUUID, data.tenant_id, orderCreationPayload)
                .then(() => {
                    openSnackbar("Data added successfully.");
                    isNext && handleNext();
                })
                .catch(function (error) {
                    console.log(error);
                });
        } else {
            createJob({customer: data.tenant_id, ...orderCreationPayload})
                .then((orderData) => {
                    setOrderId(orderData?.uuid);
                    const currentPath = window.location.pathname;
                    window.history.replaceState(
                        "",
                        "",
                        `${currentPath}?uuid=${orderData?.uuid}`
                    );
                    isNext && handleNext();
                })
                .catch(function (error) {
                    console.log(error);
                });
        }
    };

    const tapeCreation = (data, isNext) => {
        const _orderUUID = getOrderUUID();
        fetchJob(_orderUUID)
            .then((orderData) => {
                if (orderData?.customer) {
                    const orderPayload = {
                        customer: orderData.customer,
                        number_of_boxes: data.numberOfBoxes,
                        backup_type: data.backupType,
                        account: data.accountOptions,
                        volser_available: data.volserAvailable,
                    };
                    patchJob(_orderUUID, orderData.customer, orderPayload)
                        .then(() => {
                            if (!data?.tapeDetails?.length) {
                                openSnackbar("Job updated successfully");
                                isNext && handleNext();
                            }
                            (data?.tapeDetails || []).forEach((tapeDetail, index) => {
                                const tapePayload = {
                                    order: _orderUUID,
                                    type: tapeDetail.tapeType,
                                    number: tapeDetail.number,
                                    backup_format: tapeDetail.tapeFormat,
                                };
                                const tapeUuid = watch(`tape.tapeDetails[${index}].uuid`);
                                if (tapeUuid) {
                                    updateTape(_orderUUID, tapeUuid, tapePayload)
                                        .then(() => {
                                            openSnackbar("Job updated successfully");
                                            isNext && handleNext();
                                        })
                                        .catch((err) => console.log(err));
                                } else {
                                    addTape(_orderUUID, tapePayload)
                                        .then((res) => {
                                            setValue(`tape.tapeDetails[${index}].uuid`, res.uuid);
                                            openSnackbar("Job updated successfully");
                                            isNext && handleNext();
                                        })
                                        .catch((err) => console.log(err));
                                }
                            });
                        })
                        .catch((err) => {
                            openSnackbar("Error while updating job");
                            console.log(err);
                        });
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const ingestionCreation = (data, isNext) => {
        const _orderUUID = getOrderUUID();

        let payload = {
            customer: watch("customer_uuid") ? watch("customer_uuid") : customerId,
            post_ingestion: data?.postIngestion,
            tape_security_privacy: data?.tapeOptions,
        };

        if (data?.postIngestion === "destroy") {
            payload = {...payload, destroy_mode: data.destroyMode};
        }
        if (data?.postIngestion === "store") {
            payload = {
                ...payload,
                storage_duration: Number(data.storageDuration),
                storage_duration_unit: "months",
            };
        }
        if (data?.postIngestion === "return") {
            payload = {...payload, tape_return_to_sender: data.returnTo};
            if (data?.returnTo === false) {
                payload = {
                    ...payload,
                    tape_return_address: {
                        address_line_1: data.returnAddress.address_line_1,
                        address_line_2: data.returnAddress.address_line_2 || '',
                        postal_code:    data.returnAddress.postal_code,
                        country:        data.returnAddress.country,
                        city:           data.returnAddress.city || '',
                        state:          data.returnAddress.state,
                    },
                    tape_return_first_name:    data.returnContact?.first_name    ?? '',
                    tape_return_last_name:     data.returnContact?.last_name     ?? '',
                    tape_return_phone_number:  data.returnContact?.phone_number  ?? null,
                    tape_return_mobile_number: data.returnContact?.mobile_number ?? null,
                    tape_return_email:         data.returnContact?.email         ?? null,
                };
            }
        }

        fetchJob(_orderUUID)
            .then((orderData) => {
                if (orderData?.customer) {
                    payload.customer = orderData.customer;
                    patchJob(_orderUUID, orderData.customer, payload)
                        .then(() => {
                            isNext && handleNext();
                            openSnackbar("Job updated successfully");
                        })
                        .catch((err) => {
                            openSnackbar("Error while updating job");
                            console.log(err);
                        });
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    // --- Document submission ---
    const orderDocumentSubmission = (data, isNext) => {
        const _orderUUID = getOrderUUID();

        // Fetch live order status before deciding whether to transition
        // (orderDetail state may be stale — e.g. set during loadShippingData before skip)
        fetchJob(_orderUUID)
            .then((liveOrder) => {
                if (liveOrder?.status === 'draft') {
                    patchJob(_orderUUID, liveOrder.customer, {
                        status: "preparing_for_shipment",
                    }).catch(function (error) {
                        console.log(error);
                    });
                }
            })
            .catch(function (error) {
                console.log(error);
            });

        if (onComplete) {
            onComplete();
        } else {
            setSuccessDialogOpen(true);
        }
    };

    // --- Step management ---
    const handleNext = () => {
        setDisableSaveAndNext(false);
        const newActiveStep = activeStep + 1;
        if (onActiveStepChange) {
            onActiveStepChange(newActiveStep);
        } else {
            setInternalStep(newActiveStep);
            setTimeout(() => {
                loadStepData(newActiveStep);
            }, 0);
        }
    };

    const handleBack = () => {
        setDisableSaveAndNext(false);
        const newStep = activeStep - 1;
        if (onActiveStepChange) {
            onActiveStepChange(newStep);
        } else if (newStep >= 0) {
            setInternalStep(newStep);
            setTimeout(() => {
                loadStepData(newStep);
            }, 0);
        }
    };

    // --- Skip handlers ---
    const handleSkipShipping = () => {
        setIsSkipShippingDialogOpen(true);
    };

    const handleConfirmSkipShipping = async () => {
        setIsSkipShippingDialogOpen(false);
        const _orderUUID = getOrderUUID();

        try {
            const resolvedCustomerId = orderDetail?.customer || customerId;

            const result = await patchJob(_orderUUID, resolvedCustomerId, {
                shipping_mode: "none",
                chain_of_custody: getValues("chainOfCustody") || false,
                status: "audit_started",
            });

            if (result) {
                openSnackbar("Shipping skipped successfully. Job moved to Audit stage.");
                handleNext();
            }
        } catch (error) {
            console.error(error);
            openSnackbar("Error skipping shipping.");
        }
    };

    const handleSkipStep = (stepName) => {
        openSnackbar(`${stepName} step skipped successfully.`);
        handleNext();
    };


    // --- Render ---
    return (
        <>
            <Snackbar
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                bodyStyle={{backgroundColor: "yellow", color: "white"}}
                open={snackbarOpen}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
            {/* Breadcrumb for edit mode */}
            {getOrderUUID() && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: '12px', px: 4, pt: 2, pb: 1}}>
                    <img src="/assets/img/cart.svg" alt=""/>
                    <img src="/assets/img/arrow_left.svg" alt=""/>
                    <Link to='/Jobs' style={{textDecoration: 'none'}}><small className="grey-color">Your Jobs</small></Link>
                    <img src="/assets/img/arrow_left.svg" alt=""/>
                    <small className="grey-color">{getOrderUUID()}</small>
                </Box>
            )}
            <form id="mainForm" onSubmit={handleSubmit(handleFormData)}>
                <Grid container direction={"column"}>
                    {/* Stepper */}
                    {showStepper && (
                        <Grid item>
                            <Box
                                sx={{
                                    height: "80px",
                                    borderRadius: "8px 8px 0px 0px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    boxShadow: "0px 2px 6px 0px #00000040",
                                    backgroundColor: "#FFC540",
                                    justifyItems: "center",
                                }}
                            >
                                <Stepper
                                    alternativeLabel
                                    activeStep={activeStep}
                                    connector={<WizardStepConnector/>}
                                >
                                    {steps.map((step, index) => (
                                        <Step key={step}>
                                            <StepLabel StepIconComponent={WizardStepIcon}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "Poppins",
                                                        color: "#3A3E3F",
                                                        fontSize: "14px",
                                                        fontWeight: activeStep === index ? 700 : 400,
                                                        lineHeight: "14px",
                                                        letterSpacing: "0em",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {step}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                        </Grid>
                    )}
                    {/* Step content */}
                    <Grid item xs={12}>
                        {activeStep === 0 && watch("isJobStepLoading") !== true && (
                            <Job
                                register={register}
                                watch={watch}
                                setValue={setValue}
                                tenant_id={watch("tenant_id") || customerId}
                                control={control}
                                errors={errors}
                                customers={customers}
                            />
                        )}
                        {activeStep === 1 && (
                            <Shipping
                                shippingMode={shippingMode}
                                setShippingMode={setShippingMode}
                                shippingLocations={shippingLocations}
                                removeShipment={(index, data) =>
                                    handleRemoveShipment(index, data)
                                }
                                addShipment={handleSubmitShipment}
                                updateShipment={handleSubmitShipment}
                                register={register}
                                watch={watch}
                                setValue={setValue}
                                errors={errors}
                                shippingLinksFields={shippingLinksFields}
                                shippingLinksAppend={shippingLinksAppend}
                                shippingLinksRemove={(index, uuid) =>
                                    removeShipmentLink(index, uuid)
                                }
                                getValues={getValues}
                                clearAddShipmentField={clearAddShipmentField}
                                existingAddresses={[
                                    ...(customerAddress ? [customerAddress] : []),
                                    ...shippingLocations
                                        .filter(s => s.address_line_1)
                                        .map(s => ({
                                            address_uuid:   s.address_uuid,
                                            address_line_1: s.address_line_1,
                                            postal_code:    s.postal_code,
                                            city:           s.city,
                                            state:          s.state,
                                            country:        s.country,
                                            _label:         orderDetail?.customer_name || customerAddress?._label,
                                        }))
                                ]}
                                existingUsers={customerUsers}
                                onRefreshUsers={() => fetchAllCustomerContacts(orderDetail?.customer, { forceRefresh: true }).then(result => setCustomerUsers(result)).catch(() => {})}
                            />
                        )}
                        {activeStep === 2 && watch("isTapeStepLoading") !== true && (
                            <AdminTapeForm
                                orderId={orderId}
                                register={register}
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                errors={errors}
                            />
                        )}
                        {activeStep === 3 && watch("isIngestionStepLoading") !== true && (
                            <AdminIngestionForm
                                ref={ingestionFormRef}
                                register={register}
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                errors={errors}
                                existingUsers={customerUsers}
                                existingAddresses={[
                                    ...(customerAddress ? [customerAddress] : []),
                                    ...shippingLocations
                                        .filter(s => s.address_line_1)
                                        .map(s => ({
                                            address_uuid:   s.address_uuid,
                                            address_line_1: s.address_line_1,
                                            postal_code:    s.postal_code,
                                            city:           s.city,
                                            state:          s.state,
                                            country:        s.country,
                                            _label:         orderDetail?.customer_name || customerAddress?._label,
                                        }))
                                ]}
                            />
                        )}
                        {activeStep === 4 && (
                            <AdminFee
                                setDisableSaveAndNext={setDisableSaveAndNext}
                                orderId={orderId}
                                register={register}
                                control={control}
                                watch={watch}
                                setValue={setValue}
                            />
                        )}
                        {activeStep === 5 && (
                            <OrderDocuments
                                orderId={getOrderUUID()}
                                canDelete={true}
                                enableUpload={true}
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Bottom button bar */}
                <Drawer
                    variant={"persistent"}
                    anchor={"bottom"}
                    open
                    sx={{
                        "& .MuiDrawer-paper": {
                            backgroundColor: "#2C2C2C",
                            boxShadow: "0px -7px 11px 0px #00000029",
                            height: "6rem",
                            pl: "60px",
                        },
                    }}
                >
                    <Grid container direction={"row"} my={2} px={8}>
                        <Grid item xs={6} sx={flexStyles.flexRowStart}>
                            {/* Previous Button */}
                            <StyledButtonOutlined
                                variant="outlined"
                                onClick={handleBack}
                                sx={{
                                    visibility:
                                        activeStep === 0 && !onActiveStepChange
                                            ? "hidden"
                                            : "visible",
                                    width: "220px",
                                    height: "60px",
                                }}
                            >
                                Previous
                            </StyledButtonOutlined>
                        </Grid>

                        {activeStep === 5 ? (
                            <Grid item xs={6} sx={flexStyles.flexRowEnd}>
                                <StyledButtonContained
                                    variant="contained"
                                    type="submit"
                                    form="mainForm"
                                    sx={{
                                        width: "220px",
                                        height: "60px",
                                        borderRadius: "30px",
                                    }}
                                >
                                    Done
                                </StyledButtonContained>
                            </Grid>
                        ) : (
                            <Grid item xs={6} sx={flexStyles.flexRowEnd} gap={2}>
                                <Button
                                    variant="text"
                                    onClick={reset}
                                    type="button"
                                    sx={{
                                        width: "220px",
                                        color: "#FFFFFF",
                                        fontFamily: "Poppins",
                                        textTransform: "none",
                                    }}
                                >
                                    Clear
                                </Button>

                                <StyledButtonContained
                                    variant="contained"
                                    type="submit"
                                    form="mainForm"
                                    sx={{
                                        width: "220px",
                                        height: "60px",
                                    }}
                                >
                                    Save
                                </StyledButtonContained>

                                <StyledButtonContained
                                    disabled={disableSaveAndNext}
                                    variant="contained"
                                    type="submit"
                                    value="saveAndContinue"
                                    form="mainForm"
                                    sx={{
                                        width: "220px",
                                        height: "60px",
                                        borderRadius: "30px",
                                    }}
                                >
                                    {activeStep === 5 ? "Save & Submit" : "Save & Continue"}
                                </StyledButtonContained>

                                <StyledButtonContained
                                    disabled={![1, 2, 3, 4].includes(activeStep) || (activeStep === 1 && shippingLocations?.length > 0)}
                                    variant="contained"
                                    type="button"
                                    onClick={
                                        activeStep === 1
                                            ? handleSkipShipping
                                            : () => handleSkipStep(steps[activeStep])
                                    }
                                    sx={{
                                        width: "220px",
                                        height: "60px",
                                        borderRadius: "30px",
                                        opacity: ([1, 2, 3, 4].includes(activeStep) && !(activeStep === 1 && shippingLocations?.length > 0)) ? 1 : 0.5,
                                    }}
                                >
                                    Skip
                                </StyledButtonContained>
                            </Grid>
                        )}
                    </Grid>
                </Drawer>
                <ConfirmDialog
                    open={isSkipShippingDialogOpen}
                    onClose={() => setIsSkipShippingDialogOpen(false)}
                    onConfirm={handleConfirmSkipShipping}
                    message="Skipping shipping will move this job directly to the Audit stage. No shipments will be created for this job. Do you want to proceed?"
                />
            </form>
            {successDialogOpen && (
                <AdminAccountCreationSuccess
                    customerId={
                        orderDetail?.customer
                            ? orderDetail?.customer
                            : customerId
                    }
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                />
            )}
        </>
    );
};
